/*
 * Copyright (c) 2021, 2022 Tropyc Stoic (38c316a1b8d7188d059c273d892b723b)
 *
 * Permission to use, copy, modify, and distribute this software for any
 * purpose with or without fee is hereby granted, provided that the above
 * copyright notice and this permission notice appear in all copies.
 *
 * THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
 * WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
 * MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
 * ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
 * WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
 * ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
 * OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
 */
import { useState } from 'react';
import { ethers, BigNumber } from 'ethers'
import staking from './artifacts/contracts/staking.sol/Staking.json'
import ECRDummy from './artifacts/contracts/ECRDummy.sol/ECRDummy.json'
import BigNumberJS from 'bignumber.js'

BigNumberJS.set({ ROUNDING_MODE: BigNumberJS.ROUND_DOWN })

function AddLP(props) {
    const {web3, stakingContract, myAddress} = props;
    const [stake, setStake] = useState({
        paused: false,
        owner: "",
        lpToken : null,
        allowance: BigNumber.from(0),
        lpBalance: null,
        amount: "0",
        rewards: null,
        staked: null,
        removeAmount: 0,
        isLoadingMax: false,
        isLoadingMaxStaked: false,
        isClaiming: false,
        isApproving: false,
        isAdding: false,
        isLoadingLpToken: false,
        isRemoving: false,
        lpDecimals: null,
        lpSymbol: null,
        rewardDecimals:null,
        rewardSymbol: null
    })

    function bumpState(newStateChunk) {
        setStake(prev => {return {
                ...prev,
                ...newStateChunk
        }});
    }

    function handleChange(event) {
        const target = event.target;
        const value = target.value;
        const name = target.name;
        bumpState({[name]: value, valid: true})
    }

    async function loadMax() {
        const contract = new ethers.Contract(stake.lpToken, ECRDummy.abi, web3)
        bumpState({isLoadingMax: true});
        try {
            const decimals = await contract.decimals();
            const max = await contract.balanceOf(myAddress);
            const amount = amountToString(max, decimals, 10);
            bumpState({amount: amount, isLoadingMax: false});
        } catch (e) {
            console.log(e);
            bumpState({isLoadingMax: false});
        }
    }
    async function loadLpToken() {
        try {
            const contract = new ethers.Contract(stakingContract, staking.abi, web3)
            bumpState({isLoadingLpToken: true});
            const paused = await contract.paused();
            const owner = await contract.owner();
            const address = await contract.lpAddress();
            const rewardAddress = await contract.rewardAddress();
            const lpContract = new ethers.Contract(address, ECRDummy.abi, web3)
            const lpSymbol = await lpContract.symbol();
            const lpBalance = await lpContract.balanceOf(myAddress);
            const lpDecimals = await lpContract.decimals();
            const allowance = await lpContract.allowance(myAddress, stakingContract);
            const rewardContract = new ethers.Contract(rewardAddress, ECRDummy.abi, web3)
            const rewardSymbol = await rewardContract.symbol();
            const rewardDecimals = await rewardContract.decimals();
            //balance is a BigNumber
            const balance = await contract.lpStake(myAddress);
            const earned = await contract.earned(myAddress);

            bumpState({
                paused: paused,
                owner: owner,
                lpToken: address,
                lpBalance: amountToString(lpBalance, lpDecimals),
                staked: amountToString(balance, lpDecimals),
                rewards: amountToString(earned, rewardDecimals),
                allowance: allowance,
                isLoadingLpToken: false,
                lpDecimals: lpDecimals,
                lpSymbol: lpSymbol,
                rewardDecimals:rewardDecimals,
                rewardSymbol: rewardSymbol
            });
        } catch (e) {
            console.log(e);
            bumpState({isLoadingLpToken: false});
        }
    }

    async function approveLP(event) {
        event.preventDefault();

        await lpWrite(
            (contract) => contract.approve(stakingContract, ethers.constants.MaxUint256),
            (b) => ({isApproving: b}));

        await loadLpToken();
    }

    async function stakingWrite(doer, notifier) {
        return await contractWrite(stakingContract, staking.abi, doer, notifier);
    }

    async function lpWrite(doer, notifier) {
        return await contractWrite(stake.lpToken, ECRDummy.abi, doer, notifier);
    }

    async function contractWrite(contractAddress, contractAbi, doer, notifier) {
        const signer = web3.getSigner()
        const protocol = new ethers.Contract(contractAddress, contractAbi, signer)
        try{
            bumpState(notifier(true));
            const tx = await doer(protocol);
            await tx.wait();
        } catch (e) {
            //ignore
            if (e.code !== 4001)
                console.log(e);
        } finally {
            bumpState(notifier(false));
        }
    }

    async function stakingRead(doer, notifier) {
        return await contractRead(stakingContract, staking.abi, doer, notifier);
    }

//    async function lpRead(doer, notifier) {
//        return await contractRead(stake.lpToken, ECRDummy.abi, doer, notifier);
//    }
//
    async function contractRead(contractAddress, contractAbi, doer, notifier) {
        const protocol = new ethers.Contract(contractAddress, contractAbi, web3)
        try{
            bumpState(notifier(true));
            const ret = await doer(protocol);
            return ret;
        } catch (e) {
            //ignore
            if (e.code !== 4001)
                console.log(e);

            return null;
        } finally {
            bumpState(notifier(false));
        }
    }

  async function removeLP(event) {
        event.preventDefault();

        await stakingWrite(
            (contract) => contract.withdraw(toBigNumber(stake.removeAmount, stake.lpDecimals)),
            (b) => ({isRemoving: b}));

        await loadLpToken();
  }

  function toBigNumber(str, decimals) {
    if (str === "")
        return toBigNumber("0", decimals);

    return BigNumber.from(BigNumberJS(str).multipliedBy(BigNumberJS(10).pow(decimals)).toFixed());
  }

  function amountToString(amount, decimals, precision) {
    if (!amount)
        return "0";

    if (!precision)
        precision = 4;

    return BigNumberJS(amount.toString()).dividedBy(BigNumberJS(10).pow(decimals)).toFixed(precision);
  }

  async function addLP(event) {
        event.preventDefault();

        await stakingWrite(
            (contract) => contract.stake(toBigNumber(stake.amount, stake.lpDecimals)),
            (b) => ({isAdding: b}));

        await loadLpToken();
  }

  async function loadStaked(event) {
        event.preventDefault();

        const balance = await stakingRead(
            (contract) => contract.lpStake(myAddress),
            (b) => ({isLoadingMaxStaked: b}));

        bumpState({removeAmount: amountToString(balance, stake.lpDecimals, 10)});
  }

  async function claimRewards(event) {
        event.preventDefault();

        await stakingWrite(
            (contract) => contract.claim(),
            (b) => ({isClaiming: b}));

        await loadLpToken();
  }

  async function syncTreasury(event) {
        event.preventDefault();
        await stakingWrite(
            (contract) => contract.syncTreasury(),
            (b) => ({}));
  }

  async function unpause(event) {
        event.preventDefault();
        await stakingWrite(
            (contract) => contract.unpause(),
            (b) => ({}));
  }

  if (!stake.lpToken) {
    if (!stake.isLoadingLpToken) {
        loadLpToken();
    }
    return (
                            <div className="spinner-border" role="status">
                              <span className="visually-hidden">Please wait...</span>
                            </div>
    );
  }

    const rawAmount = toBigNumber(stake.amount, stake.lpDecimals);

    return (
        <div className="card">
          <div className="card-header">
            Stake <abbr title={stake.lpToken}>{stake.lpSymbol}</abbr>
          </div>
          <div className=" m-1 p-3">
        <form className="row g-3">
            { stake.paused &&
                <div className="col-md-12 alert alert-info" role="alert">
                {
                (ethers.utils.getAddress(stake.owner) === ethers.utils.getAddress(myAddress)) ?
                  <>
                  New staking paused! This probably means you moved funds from the treasury and the
                  staking contract panicked to see treasury decrease instead of increase.
                  Sync the treasury balance and unpause the staking contract.
                  <br/>
                  <br/>
                  <div className="row">
                    <div className="col-md-6">
                      <button type="button" className="btn btn-primary" onClick={syncTreasury}>Sync treasury</button>
                    </div>
                    <div className="col-md-6">
                      <button type="button" className="btn btn-primary" onClick={unpause}>Unpause</button>
                    </div>
                  </div>

                  </>
                  :
                  <>New staking paused. Ask the <abbr title={stake.owner}>owner</abbr> to resume it.</>
                }
                </div>
            }
         {/*
          <div className="col-md-12">
            <label htmlFor="lpToken" className="form-label">LP address</label>
            <input type="text" className="form-control" id="lpToken" name="lpToken" value={stake.lpToken} readOnly={true}/>
          </div>
          */}
              <div className="col-md-6">
                <label htmlFor="balance" className="form-label">Rewards: </label>
                {stake.rewards && <>
                <pre>{stake.rewards} {stake.rewardSymbol}</pre>
                {stake.isClaiming ?
                <button type="button" className="btn btn-link" disabled>Claim</button>
                :
                <button type="button" className="btn btn-link" onClick={claimRewards}>Claim</button>
                }
                <button type="button" className="btn btn-sm btn-link" onClick={loadLpToken}>Refresh</button>
                </>
                }
                {!stake.rewards && "Loading..."}
              </div>
              <div className="col-md-6">
                <label htmlFor="balance" className="form-label">Total staked: </label>
                {stake.staked && <><pre>{stake.staked} {stake.lpSymbol}</pre></>}
                {!stake.staked && "Loading..."}
              </div>
              <div className="col-md-12">
               Your balance: <pre>{stake.lpBalance} {stake.lpSymbol}</pre>
              </div>
              <div className="col-md-10">
                <label htmlFor="amount" className="form-label">Add</label>
                <input type="text" className="form-control" id="amount" name="amount" value={stake.amount} onChange={handleChange}/>
              </div>
              <div className="col-md-2">
                    <button type="button" className="btn btn-link" onClick={loadMax}>Max</button>
                {stake.isLoadingMax &&
                                    <div className="spinner-border" role="status">
                                      <span className="visually-hidden">Loading...</span>
                                    </div>
                }
              </div>
              <div className="col-5">
                {(stake.allowance.gte(rawAmount)) ?
                <button className="btn btn-secondary" disabled>1. Approved</button>
                :
                <button className="btn btn-primary" onClick={approveLP}>1. Approve</button>
                }
              </div>
              <div className="col-5">
                {(stake.allowance.gte(rawAmount)) ?
                <button type="submit" className="btn btn-primary" onClick={addLP}>2. Stake</button>
                :
                <button type="submit" className="btn btn-secondary" disabled>2. Stake</button>
                }
              </div>
              <div className="col-2">
                {(stake.isAdding || stake.isApproving) &&
                                <div className="spinner-border" role="status">
                                  <span className="visually-hidden">Please wait...</span>
                                </div>
                            }
              </div>
              <div className="col-md-10">
                <label htmlFor="removeAmount" className="form-label">Remove</label>
                <input type="text" className="form-control col-sm-10" id="removeAmount" name="removeAmount" value={stake.removeAmount} onChange={handleChange}/>
              </div>
              <div className="col-md-2">
                    <button type="button" className="btn btn-link" onClick={loadStaked}>Max</button>
                {stake.isLoadingMaxStaked &&
                                    <div className="spinner-border" role="status">
                                      <span className="visually-hidden">Loading...</span>
                                    </div>
                }
              </div>
              <div className="col-10">
                <button className="btn btn-primary" onClick={removeLP}>Remove</button>
              </div>
              <div className="col-2">
                {(stake.isRemoving) &&
                                <div className="spinner-border" role="status">
                                  <span className="visually-hidden">Please wait...</span>
                                </div>
                            }
              </div>
        </form>
        </div>
        </div>
    );
}

export default AddLP;